import { useState } from 'react';
import classNames from 'classnames';
import { t } from 'ttag';

import ThumbsUpFilledIcon from '@svg/thumbs-up-filled.svg';
import ThumbsUpIcon from '@svg/thumbs-up.svg';
import { useSocailPostDelete } from '@hook/useSocailPostDelete';
import { useSocailPostLikeToggle } from '@hook/useSocailPostLikeToggle';
import { useViewer } from '@hook/useViewer';
import { Image } from '@ui/Image';
import { Tooltip } from '@ui/Tooltip';
import { Button } from '@ui/Button';

import s from './ProfileSocialFeedPostCard.sss';

interface SocialPostAuthor {
  id: number,
  username: string,
  avatar: string,
}

export interface SocialPost {
  id: string,
  mediasUrls: string[],
  author: SocialPostAuthor,
  isLiked: boolean,
  likesCount: number,
}

interface Props {
  className?: string,
  post: SocialPost,
}

export const ProfileSocialFeedPostCard: React.FC<Props> = ({
  className,
  post,
}) => {
  const [isActionsVisible, setIsActionsVisible] = useState(false);

  const { viewer } = useViewer();

  const {
    handleDeletePost,
    loading: deletePostLoading,
  } = useSocailPostDelete(post);

  const {
    handleLikePost,
    handleUnlikePost,
    isLoading,
  } = useSocailPostLikeToggle(post);

  const handleToggleLikePost = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();

    if (post.isLiked) {
      handleUnlikePost();
    } else {
      handleLikePost();
    }
  };

  const handleDelete = (e:  React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    handleDeletePost();

    setIsActionsVisible(false);
  };

  const canDelete = viewer?.id === post.author.id;
  const canLike = viewer?.id !== post.author.id;

  return (
    <div className={classNames(s.root, className, {
      [s.rootThemeLoading]: deletePostLoading,
    })}>
      <div className={s.photoHolder}>
        <Image
          className={s.photo}
          src={post.mediasUrls[0]}
          width="284"
          height="400"
        />
      </div>

      <div className={s.author}>
        <Image
          className={s.avatar}
          src={post.author.avatar}
          width="32"
          height="32"
          lazy={true}
        />

        <div className={s.authorInfo}>
          <p className={s.username}>
            {post.author.username}
          </p>
        </div>

        {!!canDelete && (
          <Tooltip
            isVisible={isActionsVisible}
            onVisibleChange={setIsActionsVisible}
            content={(
              <ul className={s.actionsList}>
                <li className={s.actionsItem}>
                  <Button
                    theme="text"
                    className={s.actionsButton}
                    disabled={deletePostLoading}
                    onClick={handleDelete}
                  >
                    {t`Видалити`}
                  </Button>
                </li>
              </ul>
            )}
            isShowArrow={false}
            isInteractive={true}
            trigger="click"
            placement="left"
            theme="white"
            padding="16px"
          >
            <span
              className={s.dotsHolder}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <span className={s.dots} />
            </span>
          </Tooltip>
        )}

        {canLike && (
          <Button
            className={s.likes}
            theme="text"
            disabled={isLoading}
            onClick={handleToggleLikePost}
          >
            {post.likesCount}

            {post.isLiked ? (
              <ThumbsUpFilledIcon
                widths="24"
                height="24"
              />
            ) : (
              <ThumbsUpIcon
                widths="24"
                height="24"
              />
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
